import { z } from 'zod';
import {
  createExpirationDate,
  isAfterOrSameDay,
  isDateWithinYears,
} from './utils';
import { toSetError } from './zod-utils';
import { NewDiscount } from './newDiscount.viewModel';

export const formStartDate = new Date();

export const validateNewDiscountChange =
  (getCodes: () => string[]) =>
  (data: Partial<NewDiscount>, ctx: z.RefinementCtx): void => {
    const validate = toSetError(data, ctx);
    validate('name');
    validate('discountCode');
    validate('discountCode', {
      conditions: (d) => (d.discountCode || '').length < 5,
      error: () => 'Your code must be at least 5 characters long',
    });
    validate('discountCode', {
      conditions: (d) => getCodes().includes(d.discountCode || ''),
      error: () => 'Code already exists',
    });
    validate('salesType', {
      error: () => 'Select type of product',
    });

    validate('subscriptionType', {
      conditions: (d) => d.salesType === 'subscription' && !d.subscriptionType,
    });

    validate('discountType', {
      error: () => 'Select type of discount',
    });
    validate('discountFixedValue', {
      conditions: (d) =>
        d.discountType === 'FixedAmount' && !d.discountFixedValue,
      error: () => `Set discount amount`,
    });
    validate('discountPercentValue', {
      conditions: (d) =>
        d.discountType === 'Percentage' && !d.discountPercentValue,
      error: () => `Set discount percent`,
    });

    validate('expiration', {
      conditions: (d) => d.hasExpiration && !d.expiration,
      error: () => `Date is required`,
    });

    validate('expirationHour', {
      conditions: (d) => d.hasExpiration && d.expirationHour === undefined,
      error: () => `Select an hour`,
    });
    validate('expirationMinute', {
      fieldName: `Minute`,

      conditions: (d) => d.hasExpiration && d.expirationMinute === undefined,
      error: () => `Select a minute`,
    });

    validate('expirationDayTime', {
      conditions: (d) => d.hasExpiration && !d.expirationDayTime,
      error: () => `Select a daytime`,
    });

    validate('expiration', {
      conditions: (d) =>
        d.hasExpiration &&
        d.expiration &&
        d.expirationHour !== undefined &&
        d.expirationDayTime !== undefined &&
        d.expirationMinute !== undefined &&
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        createExpirationDate(d as any).getTime() <= formStartDate.getTime(),
      fieldName: `Expiration date`,
      error: (n) => `${n} has to be set in the future`,
    });

    validate('expiration', {
      conditions: (d) =>
        d.hasExpiration &&
        d.expiration &&
        !isAfterOrSameDay(d.expiration, formStartDate),
      fieldName: `Expiration date`,
      error: (n) => `${n} has to be set in the future`,
    });

    validate('expiration', {
      conditions: (d) =>
        d.hasExpiration && d.expiration && !isDateWithinYears(d.expiration, 5),
      fieldName: `Expiration date`,
      error: (n) => `${n} must be within 5 years`,
    });

    validate('redemptionsLimit', {
      conditions: (d) => d.hasRedemptionsLimit && !d.redemptionsLimit,
      error: () => 'Limit is required',
    });
    validate('redemptionsLimit', {
      conditions: (d) =>
        d.hasRedemptionsLimit && (d.redemptionsLimit as number) < 1,
      error: () => 'Limit must be bigger than 0',
    });
  };
